import I18n from 'I18n';
import { useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { useState } from 'react';
import { APP } from '../../constants/AuthType';
import { ADD_ON_CARD } from '../../v2/constants/tracking/TrackingSources';
import { useTrackingFlow } from '../../hooks/useTrackingFlow';
import { useAppSelector } from '../../reducers';
import { checkoutProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasProductVersionInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, isHubSpotCreditsSkuSelector, productForSkuIdSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector, talkToSalesTypeSelector } from '../../selectors/PurchaseMotionSelectors';
import { trackAdditionalUnitsPicker, trackBuyButtonClick, trackTalkToSalesClick, trackTooltipInteraction } from '../../v2/utils/tracking/trackingEventUtils';
import { useHasMaxProductQuantityInProductInventory } from '../../v2/hooks/inventory/useProductInventoryQuantity';
import PurchaseMotionButtonWrapper from '../common/purchaseMotions/PurchaseMotionButtonWrapper';
import { calculateMaxTouchlessPurchasableQuantity } from '../../v2/utils/quantityPackUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AddOnCardCTA = ({
  productConfigurations,
  screen,
  selectedQuantity,
  packagedQuantities,
  ownedQuantity,
  skuId,
  term,
  trackingSource = ADD_ON_CARD,
  onQuantityChange
}) => {
  const product = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const [displayedValue, setDisplayedValue] = useState(0);
  const [hasStepperBeenClicked, setHasStepperBeenClicked] = useState(false);
  const checkoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const quantifiable = useAppSelector(state => {
    const productVersion = productVersionSelector(state, {
      skuId
    });
    return !!(productVersion !== null && productVersion !== void 0 && productVersion.quantifiable);
  });
  const {
    hasMaxQuantity: ownsMaxQuantityOfProduct,
    maxQuantity: maxTouchlessPurchasableQuantity = null
  } = useHasMaxProductQuantityInProductInventory({
    apiName: product === null || product === void 0 ? void 0 : product.apiName
  });
  const ownsProduct = useAppSelector(state => hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const purchaseMotions = useAppSelector(state => {
    const motions = purchaseMotionsSelector(state, {
      skuId,
      location: 'CALCULATOR'
    });

    // we want to always fallback to showing something if there are no motions or
    // if the user owns the max quantity of the product, something is better than nothing
    if (motions.length === 0 || ownsMaxQuantityOfProduct) {
      return [talkToSalesTypeSelector(state)];
    }
    return motions;
  });
  const purchaseMotion = purchaseMotions[0];
  const isHubSpotCreditsProduct = useAppSelector(state => isHubSpotCreditsSkuSelector(state, {
    skuId
  }));
  const minValue = 0;
  const maxValue = packagedQuantities * calculateMaxTouchlessPurchasableQuantity(maxTouchlessPurchasableQuantity);
  const canBuyMultiple = quantifiable && maxValue > 1;
  const isTouchlessPurchase = purchaseMotion === 'BUY_TOUCHLESS';
  useEffect(() => {
    if (hasStepperBeenClicked) {
      setDisplayedValue(selectedQuantity * packagedQuantities);
    }
  }, [selectedQuantity, packagedQuantities, hasStepperBeenClicked]);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const trackingData = {
    app: APP,
    screen,
    uniqueId: trackingSource
  };
  const handleButtonClick = () => {
    if (isTouchlessPurchase) {
      trackBuyButtonClick({
        purchaseMotion: 'BUY_TOUCHLESS',
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : [],
        flow,
        trackingSource
      });
    } else {
      trackTalkToSalesClick({
        trackingSource,
        productNames: product ? [product.apiName] : [],
        ownedProducts: ownsProduct && product ? [product.apiName] : [],
        flow
      });
    }
  };
  const handleStepperChange = evt => {
    const newQuantity = Math.round(parseInt(evt.target.value, 10) / packagedQuantities);
    if (isNaN(newQuantity)) return;
    setHasStepperBeenClicked(true);
    setDisplayedValue(newQuantity * packagedQuantities);
    const direction = newQuantity > selectedQuantity ? 'increase' : 'decrease';
    trackAdditionalUnitsPicker({
      trackingSource,
      action: `stepper ${direction}`,
      ownedUnitQuantity: ownedQuantity,
      displayedUnitQuantity: newQuantity * packagedQuantities,
      productName: product ? product.apiName : '',
      skuId,
      ownsProduct
    });
    onQuantityChange(newQuantity);
  };

  // disable the button if not touchless or the user hasn't increased the stepper to more than already owned
  const disabledButton = isTouchlessPurchase &&
  // only disable if touchless purchase is allowed (always let talk to sales be clickable)
  selectedQuantity <= minValue;
  const disabledMinButtonTooltipKey = 'pricingPage.pages.addOns.cards.addOns.tooltip';
  const textOverride = isHubSpotCreditsProduct && ownedQuantity > 0 && isTouchlessPurchase ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "merchCards.breezeTile.buyAdditionalCredits"
  }) : undefined;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: '300',
    children: [canBuyMultiple && /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text(`pricing-pages-ui.productNamesV2.${billingNameKey}.title`),
      children: /*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx(StepperInput, {
          "data-selenium-id": `${trackingSource}-${product === null || product === void 0 ? void 0 : product.apiName}-stepper-input`,
          disabled: ownsMaxQuantityOfProduct,
          disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: disabledMinButtonTooltipKey
          }),
          disabledMinButtonTooltipProps: {
            onOpenChange: e => {
              if (e.currentTarget.value) {
                trackTooltipInteraction({
                  action: 'hovered',
                  name: `${trackingSource}-${product === null || product === void 0 ? void 0 : product.apiName}-stepper-input-disabled-tooltip`,
                  value: getI18nText(disabledMinButtonTooltipKey, {
                    locale: 'en-us'
                  }),
                  trackingSource,
                  productName: product === null || product === void 0 ? void 0 : product.apiName
                });
              }
            },
            children: /*#__PURE__*/_jsx(_Fragment, {})
          },
          stepSize: packagedQuantities,
          onChange: handleStepperChange,
          value: displayedValue,
          min: minValue,
          max: maxValue,
          inlineSize: '107px'
        })
      })
    }), /*#__PURE__*/_jsx(PurchaseMotionButtonWrapper, {
      apiName: product === null || product === void 0 ? void 0 : product.apiName,
      queryParamTerm: term,
      use: "primary",
      trackingSource: trackingSource,
      trackingData: trackingData,
      purchaseMotion: purchaseMotion,
      productConfigurations: checkoutProductConfigurations,
      disabled: disabledButton,
      onClick: handleButtonClick,
      skipInterstitialPage: true,
      purchaseMotionLocation: 'MERCH_CARD',
      children: textOverride
    })]
  });
};